<template>
  <nav class="chapter-navigation">
    <div class="lesson-arrow-toggle left">
      <span class="lesson-arrow-button lesson-arrow-button-left">
        <ArrowButton
          direction="left"
          @click="slideChapterNavigation(-1)"
          :disabled="!enableLeftNavigationArrow"
          :title="$t('question-list-manager.previous-button')"
        />
      </span>
    </div>
    <div class="chapter-navigation-outer-container">
      <div
        class="chapter-navigation-container"
        :style="{ transform: chapterNavigationCssTransform }"
      >
        <ol
          class="chapter-navigation-list"
          :style="{
            '--sections-per-page': sectionsPerNavigationPage,
            '--navigation-line-width': navigationLineWidth + '%',
            '--navigation-item-width': navigationItemWidth + '%',
          }"
        >
          <li
            v-for="item in navigationItems"
            :key="`chapter-navigation-item-${item.sectionType}-${item.id}`"
            class="chapter-navigation-item"
            :class="{
              intro: item.sectionType === ChapterSectionType.INTRO,
            }"
          >
            <RouterLink
              class="chapter-navigation-item-link"
              :to="{
                name:
                  item.sectionType === ChapterSectionType.INTRO
                    ? 'book-chapter'
                    : 'book-chapter-section',
                params: {
                  sectionSlug: createSlug(item.title, item.id),
                  sectionTypeString: item.sectionType.toString(),
                },
              }"
            >
              <ChapterSectionStatusCircle
                :chapter-id="chapterId"
                :section-type="item.sectionType"
                :section-type-id="item.id"
              />
              <p>{{ item.title }} <TextToSpeech :text="item.title" /></p>
            </RouterLink>
          </li>
        </ol>
      </div>
    </div>
    <div class="lesson-arrow-toggle right">
      <span class="lesson-arrow-button lesson-arrow-button-right">
        <ArrowButton
          direction="right"
          @click="slideChapterNavigation(1)"
          :disabled="!enableRightNavigationArrow"
          :title="$t('question-list-manager.previous-button')"
        />
      </span>
    </div>
  </nav>
</template>

<script>
import ArrowButton from '@/components/ArrowButton'
import ChapterSectionStatusCircle from '@/components/books/ChapterSectionStatusCircle'
import TextToSpeech from '@/components/accessibility/TextToSpeech'

import { createSlug } from '@/utils/string'

import { BookStoreNamespacedTypes } from '@/store/books'

import { ChapterSectionType } from '@/enums'

export default {
  name: 'ChapterNavigation',
  components: { ArrowButton, ChapterSectionStatusCircle, TextToSpeech },
  props: {
    chapterId: {
      type: Number,
      required: true,
    },
    bookId: {
      type: Number,
      required: true,
    },
    currentSectionId: {
      type: Number,
      required: false,
    },
    currentSectionType: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      ChapterSectionType,
      currentNavigationPage: 0,
      viewportWidth: 840, // 7 * 120
    }
  },
  computed: {
    /**
     * @return {Object}
     */
    chapterData() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_CHAPTER](
        this.chapterId
      )
    },

    /**
     * The CSS transform necessary for the chapter navigation to display the required page.
     * return {string}
     */
    chapterNavigationCssTransform() {
      return `translateX(${-100 * this.currentNavigationPage}%)`
    },

    /**
     * Enables the left navigation arrow when there are more pages to the left.
     * @return {boolean}
     */
    enableLeftNavigationArrow() {
      return this.currentNavigationPage > 0
    },

    /**
     * Enables the right navigation arrow when there are more pages to the right.
     * @return {boolean}
     */
    enableRightNavigationArrow() {
      return this.navigationChunks > this.currentNavigationPage + 1
    },

    /**
     * Creates sections as an array of chunks based by the size of this.sectionsPerNavigationPage
     * @return {Number}
     */
    navigationChunks() {
      return Math.ceil(
        this.navigationItems.length / this.sectionsPerNavigationPage
      )
    },

    navigationItems() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_NAVIGATION_ITEMS
      ]({ bookId: this.bookId, chapterId: this.chapterId })
    },

    currentNavigationIndex() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_NAVIGATION_ITEM_INDEX
      ]({
        bookId: this.bookId,
        chapterId: this.chapterId,
        sectionType: this.currentSectionType,
        sectionId: this.currentSectionId,
      })
    },

    /**
     * The width of a single navigation item in %
     * @return {number}
     */
    navigationItemWidth() {
      return 100 / this.sectionsPerNavigationPage
    },

    /**
     * The width of the navigation background line in %
     * @return {number}
     */
    navigationLineWidth() {
      return this.navigationItemWidth * (this.navigationItems.length - 1)
    },

    /**
     * Calculates how many sections to display on a navigation page.
     * @return {Number}
     */
    sectionsPerNavigationPage() {
      return Math.min(
        // Make sure each item has at least 120px of space
        Math.floor(this.viewportWidth / 120),
        // Don't make space for more items than we have
        this.navigationItems.length,
        // Don't display more than 7 items at the most
        7
      )
    },
  },
  watch: {
    /**
     * Focuses the navigation on the current section when the navigation index is changed.
     */
    currentNavigationIndex: {
      handler() {
        this.displayCurrentSectionInNavigation()
      },
      immediate: true,
    },

    /**
     * Focuses the navigation on the current section when the number of sections pr page is changed.
     */
    sectionsPerNavigationPage() {
      this.displayCurrentSectionInNavigation()
    },
  },
  methods: {
    createSlug,

    /**
     * If the user click a link and navigates directly to a lesson, this will slide the navigation to the current lesson/page in the slider
     */
    displayCurrentSectionInNavigation() {
      this.displaySectionInNavigation(this.currentNavigationIndex)
    },

    /**
     * Slides the chapter navigation to the given page/chunk index.
     * @param {number} pageIndex The navigation page/chunk to display.
     */
    displayNavigationPage(pageIndex) {
      this.currentNavigationPage = pageIndex
    },

    /**
     * Slides the navigation so that it displays the section with the given index.
     * @param {number} sectionIndex
     * @public
     */
    displaySectionInNavigation(sectionIndex) {
      if (this.navigationItems.length > this.sectionsPerNavigationPage) {
        this.displayNavigationPage(
          Math.floor(sectionIndex / this.sectionsPerNavigationPage)
        )
      } else {
        // Make sure the first page is displayed
        // Without this line users can end up hanging with no visible navigation items when enlarging the browser,
        // thus removing the need to divide the sections into pages.
        this.displayNavigationPage(0)
      }
    },

    /**
     * Updates the number of sections to display on a page in the navigation based on the current window width.
     */
    updateViewportWidth() {
      this.viewportWidth = window.innerWidth
    },

    /**
     * Slides the chapter navigation left or right by the given amount of pages.
     * @param {number} amount
     */
    slideChapterNavigation(amount) {
      this.displayNavigationPage(this.currentNavigationPage + amount)
    },
  },

  // Lifecycles
  mounted() {
    // Update the cached viewport width now and every time the window size changes
    this.updateViewportWidth()
    window.addEventListener('resize', this.updateViewportWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportWidth)
  },
}
</script>

<style lang="scss" scoped>
.chapter-navigation {
  --nav-margin: 4em;

  position: relative;
  display: flex;
  width: calc(100% - var(--nav-margin) * 2);
  margin: 2em var(--nav-margin);

  @media (max-width: $minWidthSmallTablet) {
    --nav-margin: 2em;
  }
}

.chapter-navigation-outer-container {
  overflow: hidden;
  width: 100%;
}

.chapter-navigation-container {
  transition: transform var(--transition-slow) ease-in-out;

  .chapter-navigation-list {
    margin: 0;
    white-space: nowrap;

    &::before {
      content: '';
      position: absolute;
      top: 1em;
      background: repeating-linear-gradient(
        90deg,
        transparent 0px,
        transparent 3px,
        $graySemiLight 3px 6px
      );
      left: calc(var(--navigation-item-width) / 2);
      height: 1px;
      transition: width 0.4s ease-in-out;
      width: var(--navigation-line-width);
    }
  }

  .chapter-navigation-item {
    position: relative;
    display: inline-block;
    margin: 0;
    width: var(--navigation-item-width);
    overflow: hidden;
    vertical-align: top;

    &.intro {
      a:not(.router-link-exact-active) {
        // This is to hit child component ChapterSectionStatusCircle
        ::v-deep .status-circle {
          background-color: $blueDark;
        }
      }
    }

    .chapter-navigation-item-link {
      display: flex;
      flex-direction: column;
      align-items: center;

      &.router-link-exact-active {
        /**progress circle outer border */
        // This is to hit child component ChapterSectionStatusCircle
        ::v-deep .status-circle {
          border: 3px solid $graySemiLight;
        }
      }

      p {
        text-align: center;
        color: $black;
        white-space: normal;
        word-break: break-word;
        hyphens: auto;
        line-height: 1.2;
        padding: 0 0.3em 0 0.3em;
      }
    }
  }
}

div.lesson-arrow-toggle {
  display: flex;
  flex-direction: column;
  margin: -3px 0;
  padding: 0;
  align-items: center;
  width: 2.2em;
  height: 80px;

  &.right {
    margin-left: auto;
  }

  .lesson-arrow-button {
    > .arrow-button {
      // !important is to override style bleeding in from the global Fessor style
      color: $graySemiLight !important;
      padding: 0;
      height: 2.5em;

      &[disabled] {
        cursor: default;
      }
    }
  }
}
</style>
